import React, { useState, useCallback, useEffect } from 'react';
import FlightListModal from './FlightListModal';
import HotelListModal from './HotelListModal';
import TransferListModal from './TransferListModal';
import { getDatePlusDays } from '../../utils/date';
import { TripInfo, Flight, Hotel, Transfer } from '../../types/Quotation';
import { QuotationRoomOccupancy, buildDefaultQuotationRooms, normalizeQuotationRooms, toTravellerInfoFromRooms } from './quotationRoomOccupancy';

export const buildFlightTravellers = (
  roomTravellers: TripInfo['travellers'],
  travellers?: TripInfo['travellers']
) => ({
  ...roomTravellers,
  infants: Number(travellers?.infants ?? roomTravellers.infants ?? 0),
});

export interface ModalCallbacks {
  openFlightModal: (leg: 'arrival' | 'departure' | 'roundTrip') => void;
  openHotelModal: (placeIdx: number) => void;
  openTransferModal: (dayIndex: number) => void;
  removeFlight: (dayIndex: number, flightIndex: number) => void;
  removeHotel: (dayIndex: number, hotelIndex: number) => void;
  removeActivity: (dayIndex: number, activityIndex: number) => void;
  removeTransfer: (dayIndex: number, transferIndex: number) => void;
}

interface ModalManagerProps {
  nightsCount: number;
  tripInfo: TripInfo;
  itinerary: any[];
  setItinerary: React.Dispatch<React.SetStateAction<any[]>>;
  startDate: string;
  travellers: TripInfo['travellers'];
  children: (callbacks: ModalCallbacks) => React.ReactNode;
  hotelCache: { data: any[]; loading: boolean; error: any | null }[];
  hotelStartDateOverride?: string;
  hotelMinStars?: number;
  hotelAdultsOverride?: number;
  hotelMealTypeOverride?: string;
  hotelRooms?: QuotationRoomOccupancy[];
  flightReselectTrigger?: number;
  onFlightReselected?: () => void;
  arrivalCityLabel?: string;
  departureCityLabel?: string;
}

const ModalManager: React.FC<ModalManagerProps> = ({
  nightsCount,
  tripInfo,
  itinerary,
  setItinerary,
  startDate,
  travellers,
  children,
  hotelCache,
  hotelStartDateOverride,
  hotelMinStars,
  hotelAdultsOverride,
  hotelMealTypeOverride,
  hotelRooms,
  flightReselectTrigger,
  onFlightReselected,
  arrivalCityLabel,
  departureCityLabel,
}) => {
  const [showFlightModal, setShowFlightModal] = useState(false);
  const [showHotelModal, setShowHotelModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedFlightDayIndex, setSelectedFlightDayIndex] = useState<number | null>(null);
  const [selectedHotelPlaceIndex, setSelectedHotelPlaceIndex] = useState<number | null>(null);
  const [selectedTransferDayIndex, setSelectedTransferDayIndex] = useState<number | null>(null);
  const [flightSelectionMode, setFlightSelectionMode] = useState<'append' | 'replace' | 'reselect'>('append');
  const [flightSelectionContext, setFlightSelectionContext] = useState<'arrival' | 'departure' | 'roundTrip'>('arrival');
  const firstPlace = tripInfo.places[0];
  const lastPlace = tripInfo.places[tripInfo.places.length - 1] || firstPlace;
  const resolvedArrivalCityLabel = (arrivalCityLabel || firstPlace?.label || '').trim();
  const resolvedDepartureCityLabel = (departureCityLabel || lastPlace?.label || arrivalCityLabel || '').trim();

  const openFlightModal = useCallback((leg: 'arrival' | 'departure' | 'roundTrip') => {
    setFlightSelectionContext(leg);
    const dayIndex = leg === 'departure' ? nightsCount : 0;
    setSelectedFlightDayIndex(dayIndex);
    const hasFlightsAtTarget = leg === 'roundTrip'
      ? (itinerary[0]?.flights || []).length > 0 || (itinerary[nightsCount]?.flights || []).length > 0
      : (itinerary[dayIndex]?.flights || []).length > 0;
    setFlightSelectionMode(hasFlightsAtTarget ? 'replace' : 'append');
    setShowFlightModal(true);
  }, [itinerary, nightsCount]);

  const addFlightAt = useCallback((dayIndex: number, flight: Flight) => {
    setItinerary(prev =>
      prev.map((item, idx) => (idx !== dayIndex ? item : { ...item, flights: [...item.flights, flight] }))
    );
  }, [setItinerary]);

  const replaceFlightAt = useCallback((dayIndex: number, flight: Flight) => {
    setItinerary((prev) =>
      prev.map((item, idx) => (idx !== dayIndex ? item : { ...item, flights: [flight] }))
    );
  }, [setItinerary]);

  const removeFlight = useCallback((dayIndex: number, flightIndex: number) => {
    setItinerary(prev =>
      prev.map((item, idx) =>
        idx !== dayIndex
          ? item
          : { ...item, flights: item.flights.filter((_:any, i:any) => i !== flightIndex) }
      )
    );
  }, [setItinerary]);

  const removeHotel = useCallback((dayIndex: number, hotelIndex: number) => {
    setItinerary(prev =>
      prev.map((item, idx) =>
        idx !== dayIndex
          ? item
          : { ...item, hotels: item.hotels.filter((_: any, i: any) => i !== hotelIndex) }
      )
    );
  }, [setItinerary]);

  const removeActivity = useCallback((dayIndex: number, activityIndex: number) => {
    setItinerary(prev =>
      prev.map((item, idx) =>
        idx !== dayIndex
          ? item
          : { ...item, activities: item.activities.filter((_:any, i:any) => i !== activityIndex) }
      )
    );
  }, [setItinerary]);

  const removeTransfer = useCallback((dayIndex: number, transferIndex: number) => {
    setItinerary(prev =>
      prev.map((item, idx) =>
        idx !== dayIndex
          ? item
          : { ...item, transfers: (item.transfers || []).filter((_: any, i: any) => i !== transferIndex) }
      )
    );
  }, [setItinerary]);

  const openHotelModal = useCallback((placeIdx: number) => {
    setSelectedHotelPlaceIndex(placeIdx);
    setShowHotelModal(true);
  }, []);


  const openTransferModal = useCallback((dayIndex: number) => {
    setSelectedTransferDayIndex(dayIndex);
    setShowTransferModal(true);
  }, []);

  const hotelBaseDate = hotelStartDateOverride || startDate;
  const tripEndDate = getDatePlusDays(startDate, nightsCount);
  const roomSelection = normalizeQuotationRooms(hotelRooms || buildDefaultQuotationRooms());
  const roomTravellers = toTravellerInfoFromRooms(roomSelection);
  const flightTravellers = buildFlightTravellers(roomTravellers, travellers);

  const getPlaceStayRange = useCallback((placeIdx: number) => {
    const offsetDays = tripInfo.places
      .slice(0, placeIdx)
      .reduce((sum, place) => sum + Math.max(0, Number(place.nights || 0)), 0);
    const nights = Math.max(1, Number(tripInfo.places[placeIdx]?.nights || 0));
    return { startDayIndex: offsetDays, endDayIndex: offsetDays + nights };
  }, [tripInfo.places]);

  useEffect(() => {
    if (!showHotelModal || selectedHotelPlaceIndex === null) return;
    if (tripInfo.places[selectedHotelPlaceIndex]) return;
    setShowHotelModal(false);
    setSelectedHotelPlaceIndex(null);
  }, [selectedHotelPlaceIndex, showHotelModal, tripInfo.places]);

  const handleSelectHotel = useCallback((hotel: Hotel) => {
    const placeIdx = selectedHotelPlaceIndex ?? 0;
    const place = tripInfo.places[placeIdx];
    if (!place) {
      setShowHotelModal(false);
      setSelectedHotelPlaceIndex(null);
      return;
    }
    // Compute check-in/out dates for this place
    const { startDayIndex, endDayIndex } = getPlaceStayRange(placeIdx);
    const offsetDays = startDayIndex;
    const placeCheckIn = getDatePlusDays(hotelBaseDate, offsetDays);
    const placeCheckOut = getDatePlusDays(placeCheckIn, Math.max(1, Number(place.nights || 0)));

    const hotelWithDates: Hotel = {
      ...hotel,
      checkInDate: placeCheckIn,
      checkOutDate: placeCheckOut,
    };

    setItinerary(prev =>
      prev.map((item, idx) =>
        idx < startDayIndex || idx >= endDayIndex
          ? item
          : { ...item, hotels: [hotelWithDates] }
      )
    );
    setShowHotelModal(false);
    setSelectedHotelPlaceIndex(null);
  }, [selectedHotelPlaceIndex, setItinerary, hotelBaseDate, tripInfo.places, getPlaceStayRange]);


  return (
    <>
      {children({ openFlightModal, openHotelModal, openTransferModal, removeFlight, removeHotel, removeActivity, removeTransfer })}

      {showFlightModal && selectedFlightDayIndex !== null && (
        <FlightListModal
          startDate={flightSelectionContext === 'departure' ? tripEndDate : startDate}
          from={flightSelectionContext === 'roundTrip' || flightSelectionContext === 'arrival' ? tripInfo.from : undefined}
          to={flightSelectionContext === 'departure' ? tripInfo.from : undefined}
          travellers={flightTravellers}
          defaultToCityLabel={flightSelectionContext === 'roundTrip' || flightSelectionContext === 'arrival'
            ? resolvedArrivalCityLabel
            : tripInfo.from?.label}
          defaultFromCityLabel={flightSelectionContext === 'departure' ? resolvedDepartureCityLabel : undefined}
          roundTripDefault={flightSelectionContext === 'roundTrip'}
          returnDate={flightSelectionContext === 'roundTrip' ? tripEndDate : undefined}
          disableOneWay={flightSelectionContext === 'roundTrip'}
          disableRoundTrip={flightSelectionContext !== 'roundTrip'}
          onClose={() => {
            setShowFlightModal(false);
            setSelectedFlightDayIndex(null);
            setFlightSelectionContext('arrival');
            setFlightSelectionMode('append');
          }}
          onSelectOutbound={(flight) => {
            const targetDayIndex = flightSelectionContext === 'departure' ? nightsCount : 0;
            if (flightSelectionMode === 'reselect' || flightSelectionMode === 'replace') {
              replaceFlightAt(targetDayIndex, flight);
              if (flightSelectionMode === 'reselect') {
                onFlightReselected?.();
              }
              return;
            }
            addFlightAt(targetDayIndex, flight);
          }}
          onSelectReturn={(flight) => {
            if (flightSelectionContext !== 'roundTrip') return;
            const targetDayIndex = nightsCount;
            if (flightSelectionMode === 'reselect' || flightSelectionMode === 'replace') {
              replaceFlightAt(targetDayIndex, flight);
              if (flightSelectionMode === 'reselect') {
                onFlightReselected?.();
              }
              return;
            }
            addFlightAt(targetDayIndex, flight);
          }}
        />
      )}

      {showHotelModal && selectedHotelPlaceIndex !== null && (() => {
        const place = tripInfo.places[selectedHotelPlaceIndex];
        if (!place) return null;
        const offsetDays = tripInfo.places
          .slice(0, selectedHotelPlaceIndex)
          .reduce((sum, p) => sum + p.nights, 0);
        const placeCheckIn = getDatePlusDays(hotelBaseDate, offsetDays);
        const placeCheckOut = getDatePlusDays(placeCheckIn, place.nights);
        const hotelTravellers = hotelAdultsOverride !== undefined
          ? { ...roomTravellers, adults: hotelAdultsOverride }
          : roomTravellers;
        return (
          <HotelListModal
            place={place}
            checkIn={placeCheckIn}
            checkOut={placeCheckOut}
            travellers={hotelTravellers}
            rooms={roomSelection}
            onClose={() => setShowHotelModal(false)}
            onSelect={handleSelectHotel}
            initialHotels={hotelCache[selectedHotelPlaceIndex]?.data}
            initialLoading={hotelCache[selectedHotelPlaceIndex]?.loading}
            initialError={hotelCache[selectedHotelPlaceIndex]?.error}
            defaultMinStars={hotelMinStars}
            mealTypeOverride={hotelMealTypeOverride}
          />
        );
      })()}


      {showTransferModal && selectedTransferDayIndex !== null && (() => {
        const dayIndex = selectedTransferDayIndex;
        const defaultDate = getDatePlusDays(startDate, dayIndex);
        // Map day index to place index by nights; last (departure) day maps to last place
        const placeBoundaries = tripInfo.places.reduce<{ start: number; end: number; idx: number }[]>((acc, p, idx) => {
          const start = acc.length === 0 ? 0 : acc[acc.length - 1].end;
          const end = start + Math.max(0, p.nights || 0);
          acc.push({ start, end, idx });
          return acc;
        }, []);
        const findPlaceIdx = (di: number): number => {
          const hit = placeBoundaries.find(b => di >= b.start && di < b.end);
          if (hit) return hit.idx;
          // If outside ranges (e.g., last departure day), use last place
          return Math.max(0, tripInfo.places.length - 1);
        };
        const placeIdx = findPlaceIdx(dayIndex);
        const place = tripInfo.places[placeIdx] || { label: 'Unknown', value: '' };
        const hotelsForDay: Hotel[] = itinerary[placeIdx]?.hotels || [];
        const flightsFlat: Flight[] = itinerary.flatMap((d: any) => d.flights || []);
        const uniquePlaces = tripInfo.places.filter((p, idx, arr) => arr.findIndex(q => q.value === p.value) === idx);

        const handleSelectTransfer = (t: Transfer) => {
          setItinerary(prev => prev.map((item, idx) => (
            idx !== dayIndex
              ? item
              : { ...item, transfers: [...(item.transfers || []), t] }
          )));
          setShowTransferModal(false);
          setSelectedTransferDayIndex(null);
        };

        return (
          <TransferListModal
            dayIndex={dayIndex}
            defaultDate={defaultDate}
            defaultPlace={place}
            travellers={travellers}
            placeOptions={uniquePlaces}
            flights={flightsFlat}
            hotelsForDay={hotelsForDay}
            onClose={() => {
              setShowTransferModal(false);
              setSelectedTransferDayIndex(null);
            }}
            onSelect={handleSelectTransfer}
          />
        );
      })()}
    </>
  );
};

export default ModalManager;
